import { Theme } from "@mui/material/styles";

export interface SupplyColorCodingOptions {
  value: number;
  targetValue: number | null;
  theme: Theme;
}

/**
 * WOS band for a value vs target. Thresholds are the product's canonical
 * vs-target bands — do not change them when restyling fills.
 */
export type SupplyBand = "critical" | "low" | "warn" | "ok" | "excess";

export const getSupplyBand = (
  value: number,
  targetValue: number | null
): SupplyBand | undefined => {
  if (targetValue === null || targetValue === undefined || isNaN(targetValue)) {
    return undefined;
  }
  if (value === 0) return "critical";
  if (value > 3 * targetValue) return "excess";
  if (value >= targetValue) return "ok";
  if (value >= 0.5 * targetValue) return "warn";
  if (value > 0) return "low";
  return "critical";
};

// Soft band tints (readability pass): pale fills keep the number legible
// instead of a solid saturated slab. The saturated hue survives as the
// keyline color on worst-band cells (see getSupplyCellKeyline).
const BAND_FILLS: Record<SupplyBand, string> = {
  critical: "#FAEAE6",
  low: "#FAEAE6",
  warn: "#FFF7D9",
  ok: "#EFF5E4",
  excess: "#F3E8FD",
};


export const getSupplyCellBackgroundColor = ({
  value,
  targetValue,
}: SupplyColorCodingOptions): string | undefined => {
  const band = getSupplyBand(value, targetValue);
  return band ? BAND_FILLS[band] : undefined;
};


export const getWOSMeasureKey = (
  base: "target" | "starting" | "ending",
  dateHierarchy: string
): string => {
  const period = getPeriodLabel(dateHierarchy);
  return `${base} ${period.toLowerCase()} of supply`;
};

export const getPeriodLabel = (dateHierarchy: string): string => {
  if (dateHierarchy.includes("day")) return "Days";
  if (dateHierarchy.includes("week")) return "Weeks";
  if (dateHierarchy.includes("month")) return "Months";
  return "Periods";
};
